import React, { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  DEFAULT_PRINT_SECTIONS,
  PRINT_SECTIONS,
  PostPrint,
  PrintSections,
  hasPrintSection,
} from '../../components/feed/PostPrint'
import { Button, ButtonVariant } from '../../components/common/Button'
import { LinkButton } from '../../components/common/LinkButton'
import { IconChoices } from '../../components/common/Icon'
import { Spinner } from '../../components/common/Spinner'
import { SpinnerWrapper } from '../../components/common/SpinnerWrapper'
import { Checkbox } from '../../components/common/Checkbox'
import { useAppTranslation } from '../../hooks/useAppTranslation'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { formatDateWithoutTime } from '../../helpers/date'
import { isPostAccessError, postRowQueries } from '../../api/posts'
import { commentQueries } from '../../api/comments'
import { reverseUrl } from '../../navigation/reverseUrl'
import { useCanPrintPost } from '../../components/feed/PostPrint/useCanPrintPost'

export const PostPrintView = (): React.ReactNode => {
  const { id } = useParams()
  const { translate } = useAppTranslation()
  const [sections, setSections] = useState<PrintSections>(
    DEFAULT_PRINT_SECTIONS
  )
  const showComments = sections.comments
  const { data: row, isLoading, error } = useQuery(postRowQueries.row(id))

  const post = isPostAccessError(error) ? undefined : row
  const { canPrint, isLoading: isLoadingRole } = useCanPrintPost(
    post?.society.id
  )
  const hasComments = !!post && canPrint && post.comments.count > 0

  const discussion = useInfiniteQuery({
    ...commentQueries.discussion(post?.id),
    enabled: hasComments && showComments,
  })
  const {
    data: discussionData,
    isFetching,
    isError: commentsFailed,
    hasPreviousPage,
    hasNextPage,
    fetchPreviousPage,
    fetchNextPage,
  } = discussion

  // The first page opens on the latest comments; walk both ways until the
  // whole discussion is loaded so the printout is complete.
  useEffect(() => {
    if (!showComments || !discussionData || isFetching || commentsFailed) {
      return
    }
    if (hasPreviousPage) {
      fetchPreviousPage()
    } else if (hasNextPage) {
      fetchNextPage()
    }
  }, [
    showComments,
    discussionData,
    isFetching,
    commentsFailed,
    hasPreviousPage,
    hasNextPage,
    fetchPreviousPage,
    fetchNextPage,
  ])

  useDocumentTitle(
    post
      ? `${post.society.name} · ${formatDateWithoutTime(post.createdAt)}`
      : 'Boappa'
  )

  if (!id) {
    return <Navigate to="/not-found" replace />
  }

  if (isLoading || isLoadingRole) {
    return <SpinnerWrapper />
  }

  if (!post) {
    return (
      <p className="p-8 text-center">
        {translate(
          error && !isPostAccessError(error)
            ? 'flashMessage.somethingWentWrongError'
            : 'postView.errorView.removed.title'
        )}
      </p>
    )
  }

  if (!canPrint) {
    return <Navigate to={reverseUrl('post:detail', { id })} replace />
  }

  const commentsComplete =
    !!discussionData && !hasPreviousPage && !hasNextPage && !isFetching

  let commentsStatus: React.ReactNode
  if (commentsFailed) {
    commentsStatus = (
      <div className="flex flex-col items-start gap-2 py-3">
        <p>{translate('flashMessage.somethingWentWrongError')}</p>
        <Button
          className="flex w-fit"
          variant={ButtonVariant.TEXT}
          label={translate('common.actions.tryAgain')}
          onClick={() => discussion.refetch()}
        />
      </div>
    )
  } else if (!commentsComplete) {
    commentsStatus = (
      <div className="flex justify-center py-3">
        <Spinner />
      </div>
    )
  }

  const showCommentsSection = hasComments && showComments

  const availableSections = PRINT_SECTIONS.filter((section) =>
    section === 'comments' ? hasComments : hasPrintSection(post, section)
  )

  let printBlockedReason: string | undefined
  if (availableSections.every((section) => !sections[section])) {
    printBlockedReason = translate('postView.print.nothingSelected')
  } else if (showCommentsSection && commentsFailed) {
    printBlockedReason = translate('postView.print.commentsFailed')
  } else if (showCommentsSection && !commentsComplete) {
    printBlockedReason = translate('postView.print.commentsLoading')
  }

  return (
    <div className="flex-1 overflow-y-auto bg-white px-4 py-6 print:h-auto print:overflow-visible print:p-0">
      <div className="mx-auto mb-4 max-w-[180mm] lg:max-w-[calc(180mm_+_18rem)] print:hidden">
        <LinkButton
          className="hover:bg-neutral-92"
          variant={ButtonVariant.TEXT}
          label={translate('feedView.title')}
          icon={IconChoices.ARROW_LEFT}
          size="sm"
          url={reverseUrl('feed')}
          urlIsExternal={false}
        />
      </div>
      <div className="mx-auto flex max-w-[180mm] flex-col gap-6 lg:max-w-[calc(180mm_+_18rem)] lg:flex-row-reverse lg:items-start print:block print:max-w-none">
        <aside className="flex shrink-0 flex-col gap-4 rounded-md border border-neutral-80 p-4 lg:sticky lg:top-0 lg:w-64 print:hidden">
          <h2 className="text-base font-semibold">
            {translate('postView.print.content')}
          </h2>
          <div className="flex flex-col gap-3">
            {availableSections.map((section) => (
              <label
                key={section}
                htmlFor={`print-section-${section}`}
                className="flex cursor-pointer items-center gap-3"
              >
                <Checkbox
                  name={`print-section-${section}`}
                  value={sections[section]}
                  onChange={(e) => {
                    const { checked } = e.currentTarget
                    setSections((current) => ({
                      ...current,
                      [section]: checked,
                    }))
                  }}
                />
                <span>{translate(`postView.print.sections.${section}`)}</span>
              </label>
            ))}
          </div>
          <Button
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            icon={IconChoices.PRINT}
            label={translate('common.actions.print')}
            disabled={!!printBlockedReason}
            onClick={() => window.print()}
          />
          {printBlockedReason && (
            <p className="text-sm text-neutral-40">{printBlockedReason}</p>
          )}
        </aside>
        <div className="min-w-0 flex-1">
          <PostPrint
            row={post}
            sections={sections}
            comments={
              showCommentsSection
                ? discussionData?.pages.flatMap((page) => page.items) ?? []
                : undefined
            }
            commentsStatus={showCommentsSection ? commentsStatus : undefined}
          />
        </div>
      </div>
    </div>
  )
}
